import { useCallback, useEffect, useState } from 'react'
import { Trans } from 'react-i18next'

import { Trash16 } from '@makyoapp/makyo-icon/icon'
import { Button, ButtonColor, ButtonVariant, Modal } from '@makyoapp/makyo-ui'

import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { EVENT_KEYS } from '@/shared/custom-event'

import { useGridV2Context } from '../../contexts/GridV2Context'

type AlertType = 'none' | 'snapshot-column-used'

interface RelatedTableInfo {
  id: string
  name: string
  type: 'Snapshot' | 'Query'
}

interface IState {
  isOpen: boolean
  type: AlertType
  columnId: string
  affectedTables: RelatedTableInfo[]
}

export default function SnapshotAlertModal() {
  const { dbClient } = useDbClientContext()
  const { activeTable } = useGridV2Context()

  const [state, setState] = useState<IState>({
    isOpen: false,
    type: 'none',
    affectedTables: [],
    columnId: '',
  })
  const [isLoading, setIsLoading] = useState(false)

  const handleOpenModal = useCallback(
    (e: Event) => {
      if (!dbClient || !activeTable) return
      const { columnId, type } = (e as CustomEvent).detail

      let affectedTables: RelatedTableInfo[] = []

      const snapshotTables = dbClient.snapshotRefMap.get(activeTable.id) ?? new Set()
      const queryTables = dbClient.queryRefMap.get(activeTable.id) ?? new Set()
      const relatedTables = Array.from([...snapshotTables, ...queryTables])

      if (relatedTables && relatedTables.length > 0) {
        affectedTables =
          relatedTables
            .map((id) => dbClient.tablesMap.get(id)!)
            .map((tb) => ({
              id: tb.id,
              name: tb.settings._Name,
              type: tb.isSnapshot ? 'Snapshot' : 'Query',
            })) ?? []
      }

      setState({
        isOpen: true,
        type,
        columnId,
        affectedTables,
      })
    },
    [activeTable, dbClient]
  )

  // open modal from custom-event listener
  useEffect(() => {
    window.addEventListener(EVENT_KEYS.SNAPSHOT_ALERT_MODAL, handleOpenModal)
    return () => window.removeEventListener(EVENT_KEYS.SNAPSHOT_ALERT_MODAL, handleOpenModal)
  }, [handleOpenModal])

  const handleClose = () => {
    setState({
      isOpen: false,
      type: 'none',
      affectedTables: [],
      columnId: '',
    })
  }

  const handleConfirmDelete = () => {
    setIsLoading(true)

    activeTable
      ?.DeleteColumn(state.columnId)
      .then(() => {
        handleClose()

        setState({
          isOpen: false,
          type: 'none',
          affectedTables: [],
          columnId: '',
        })
      })
      .catch((err) => {
        //
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  return (
    <Modal show={state.isOpen} onHide={handleClose} closeOnClickOutside={false}>
      <Modal.Header onCloseClick={handleClose}>
        <Trans>Unable to Delete Column</Trans>
      </Modal.Header>

      <Modal.Body className='modal-md'>
        <div>
          <Trans>
            <strong>Warning:</strong> Deleting this column will also <strong>permanently delete</strong> the following
            columns and all their data. This action cannot be undone.
          </Trans>

          <div className='mt-4 max-h-[200px] overflow-y-auto'>
            <span className='text-light'>Affected Tables:</span>
            <ul className='list-disc mt-2 pl-6 space-y-1'>
              {state.affectedTables.map((table) => (
                <li key={table.id}>
                  <b>{table.name}</b> <span className='text-light'>({table.type})</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className='flex justify-between'>
        <Button variant={ButtonVariant.Outlined} colorStyle={ButtonColor.Secondary} onClick={handleClose}>
          <Trans>Close</Trans>
        </Button>

        <Button
          icon={<Trash16 className='shrink-0' />}
          colorStyle={ButtonColor.Secondary}
          onClick={handleConfirmDelete}
          disabled={isLoading}
        >
          {isLoading ? <span className='loading spin' /> : <Trans>Delete All Columns</Trans>}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
